import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  Button,
  IconButton,
  CircularProgress
} from "@mui/material";

import SearchIcon from "@mui/icons-material/Search";
import SortIcon from "@mui/icons-material/Sort";
import AddIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import { ToggleButtonGroup, ToggleButton } from "@mui/material";
import GridViewOutlinedIcon from "@mui/icons-material/GridViewOutlined";
import ViewListOutlinedIcon from "@mui/icons-material/ListAltOutlined";

import nodatafound from "../../../utilities/dummy_assets/nodatafound.jpg";


import QuizContent from "./QuizContent";
import AddQuizForm from "./AddQuizForm";
import QuizDetailPage from "./QuizDetailPage";


import AdminQuizAPIs from "../../../utilities/api/quiz/AdminQuizAPIs";

const QuizPage = () => {
  const [activeView, setActiveView] = useState(0);
  const [search, setSearch] = useState("");

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  const [isCreating, setIsCreating] = useState(false);
  const [selectedQuiz, setSelectedQuiz] = useState(null);
  const [mode, setMode] = useState("list"); // list | view | edit | create

  /* ================= FETCH QUIZZES ================= */

  const fetchQuizzes = async () => {
    try {
      setLoading(true);
      const res = await AdminQuizAPIs.QuizListGet();
      setData(res || []);
    } catch (err) {
      console.error("Failed to load quizzes", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchQuizzes();
  }, []);

  /* ================= HANDLERS ================= */

  const handleCreate = () => {
    setMode("create");
  };

  const handleView = async quiz => {
    try {
      const questions = await AdminQuizAPIs.GetQuizQuestions(quiz.id);

      setSelectedQuiz({
        ...quiz,
        questions
      });

      setMode("view");

    } catch (err) {
      console.error(err);
      alert("Failed to load quiz questions");
    }
  };

  const handleEdit = async quiz => {
    try {
      const questions = await AdminQuizAPIs.GetQuizQuestions(quiz.id);

      setSelectedQuiz({
        ...quiz,
        questions
      });

      setMode("edit");

    } catch (err) {
      console.error(err);
      alert("Failed to load quiz questions");
    }
  };

  const handleDelete = async quiz => {
    const confirmDelete = window.confirm(
      `Delete "${quiz.title}"?`
    );
    if (!confirmDelete) return;

    try {
      await AdminQuizAPIs.DeleteQuiz(quiz.id);
      fetchQuizzes();
    } catch (err) {
      console.error(err);
      alert("Failed to delete quiz");
    }
  };

  const handleBackToList = () => {
    setMode("list");
    setSelectedQuiz(null);
    fetchQuizzes();
  };

  /* ================= MODE RENDERING ================= */

  if (mode === "create") {
    return <AddQuizForm onBack={handleBackToList} />;
  }

  // 🔜 Later you can create ViewQuizPage / EditQuizPage
  if (mode === "view") {
    return (
      <QuizDetailPage
        quiz={selectedQuiz}
        onBack={handleBackToList}
        startEdit={false}
      />
    );
  }

  if (mode === "edit") {
    return (
      <QuizDetailPage
        quiz={selectedQuiz}
        onBack={handleBackToList}
        startEdit={true}
      />
    );
  }


  /* ================= LIST PAGE ================= */

  return (
    <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
      <Box sx={{ flex: 1, p: 2, minWidth: 0, }}>

        {/*  Search + Controls */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 3,
            gap: 2,
            flexWrap: 'wrap',
            minWidth: 0,
          }}
        >
          {/* LEFT */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', }}>
            <TextField
              placeholder="Search...."
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: "#7A7A7A" }} />
                  </InputAdornment>
                )
              }}
              sx={{
                width: {
                  xs: '100%',
                  sm: 260,
                  md: 350,
                },
                flexShrink: 1,
                minWidth: 0,

                '& .MuiOutlinedInput-root': {
                  border: '1px solid #E6E6E6',
                  backgroundColor: '#FAFAFA',
                  color: '#7A7A7A',
                  borderRadius: '8px',
                  fontSize: '16px',
                  '& .MuiOutlinedInput-input': {
                    paddingY: '14px'
                  }
                },
              }}
              onChange={e => setSearch(e.target.value)}
            />



          </Box>

          {/* RIGHT */}
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              sx={{
                backgroundColor: '#3F3F3F',
                minWidth: '170px',
                padding: "12px 16px",
                color: '#fff',
                textTransform: 'none',
                fontWeight: 500,
                fontSize: '16px',
                borderRadius: '12px',
                '&:hover': {
                  backgroundColor: '#222',
                },
              }}
              onClick={handleCreate}
            >
              Add Quiz
            </Button>

            {/* View Toggles */}

            {/* Grid View */}

            <Box sx={{ display: "flex", gap: 1 }}>
              {/* Grid View */}
              <IconButton
                onClick={() => setActiveView(0)}
                sx={{
                  height: 48,
                  width: 48,
                  p: '0',
                  borderRadius: "8px",
                  border: "1px solid #B5B5B5",
                  backgroundColor:
                    activeView === 0 ? "#5C5C5C" : "#F4F4F4",
                  color: activeView === 0 ? "#fff" : "#5C5C5C",
                  "&:hover": {
                    backgroundColor:
                      activeView === 0 ? "#5C5C5C" : "#EAEAEA",
                  },
                }}
              >
                <GridViewOutlinedIcon />
              </IconButton>

              {/* List View */}
              <IconButton
                onClick={() => setActiveView(1)}
                sx={{
                  height: 48,
                  width: 48,
                  p: '0',
                  borderRadius: "8px",
                  border: "1px solid #B5B5B5",
                  backgroundColor:
                    activeView === 1 ? "#5C5C5C" : "#F4F4F4",
                  color: activeView === 1 ? "#fff" : "#5C5C5C",
                  "&:hover": {
                    backgroundColor:
                      activeView === 1 ? "#5C5C5C" : "#EAEAEA",
                  },
                }}
              >
                <ViewListOutlinedIcon />
              </IconButton>
            </Box>


          </Box>
        </Box>

        {/* 📦 Content */}
        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "60vh",
            }}
          >
            <CircularProgress />
          </Box>
        ) : data.length > 0 ? (
          <QuizContent
            data={data}
            searchQuery={search}
            activeView={activeView}
            onView={handleView}
            onEdit={handleEdit}
            onDelete={handleDelete}
          />
        ) : (
          <Box textAlign="center" mt={10}>
            <img
              src={nodatafound}
              alt="No data"
              style={{ maxHeight: 300 }}
            />
            <Typography>No quizzes available</Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default QuizPage;